import { effect, shallowRef, stop } from '@vue/reactivity'
import { cleanupComponentsIn, mergePreservingGetters, reviveComponentsIn } from '../component/reactive.js'
import { evaluateInScope } from '../component/utils.js'
import { parseDirectives } from '../component/init.js'

export default {
    signature: 'qv-for',

    handle(node, scope) {

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null
        }

        const expression = node.getAttribute(this.signature)

        if (!expression) {
            return
        }

        // Parse expression like: "item in items" or "(key, value) in object"
        const match = expression.match(/^(?:\((\w+)\s*,\s*(\w+)\)|(\w+))\s+in\s+(\S+)$/)

        if (!match) {

            console.error(`Invalid qv-for expression: "${ expression }"`)

            return

        }

        const itemAlias = match[ 1 ] || match[ 3 ]
        const indexAlias = match[ 2 ] || '$index'
        const listExpression = match[ 4 ]
        const anchor = document.createComment('qv-for anchor')

        node.parentNode.insertBefore(anchor, node)
        node.remove()

        // Store both nodes and their cleanup callbacks
        const renderedItems = new Map()

        let lastRenderedNode = anchor // Track the last insertion point

        const runner = effect(() => {

            const list = evaluateInScope(listExpression, scope)
            const templateNode = node.cloneNode(true)

            templateNode.removeAttribute('qv-for')

            const currentKeys = new Set()
            const entries = []

            if (Array.isArray(list)) {

                for (const [ index, item ] of list.entries()) {
                    entries.push([ index, item ])
                }

            } else if (list && typeof list === 'object') {

                for (const [ key, value ] of Object.entries(list)) {
                    entries.push([ key, value ])
                }

            }

            lastRenderedNode = anchor

            for (const [ key, value ] of entries) {

                currentKeys.add(key)

                if (renderedItems.has(key)) {

                    const existingItem = renderedItems.get(key)

                    const newScope = mergePreservingGetters({}, scope, {
                        [ itemAlias ]: value,
                        [ indexAlias ]: key,
                    })

                    runCleanupCallbacks(existingItem.cleanup)

                    /*
                     * Keep the same holder reference so child <script arbor :prop> bindings
                     * re-evaluate against the new iteration scope without being torn down.
                     */
                    if (existingItem.node._qvScopeHolder) {
                        existingItem.node._qvScopeHolder.value = newScope
                    } else {
                        existingItem.node._qvScopeHolder = shallowRef(newScope)
                    }

                    const cleanupCallbacks = parseDirectives(existingItem.node, newScope)

                    renderedItems.set(key, {
                        node: existingItem.node,
                        cleanup: cleanupCallbacks,
                    })

                    /*
                     * Object keys can reorder between ticks; move reused nodes
                     * so DOM order tracks iteration order.
                     */
                    const expectedNode = lastRenderedNode.nextSibling

                    if (existingItem.node !== expectedNode) {
                        anchor.parentNode.insertBefore(existingItem.node, expectedNode)
                    }

                    lastRenderedNode = existingItem.node

                    continue

                }

                const { node: itemNode, cleanupCallbacks } = createItemNode(
                    templateNode,
                    anchor.parentNode,
                    lastRenderedNode,
                    { [ itemAlias ]: value, [ indexAlias ]: key },
                    scope,
                )

                lastRenderedNode = itemNode

                renderedItems.set(key, {
                    node: itemNode,
                    cleanup: cleanupCallbacks,
                })

            }

            for (const [ key, item ] of renderedItems.entries()) {

                if (currentKeys.has(key)) {
                    continue
                }

                removeRenderedItem(item)
                renderedItems.delete(key)

            }

        })

        return () => {

            stop(runner)

            for (const item of renderedItems.values()) {
                removeRenderedItem(item)
            }

            renderedItems.clear()

            if (anchor.parentNode) {
                anchor.parentNode.replaceChild(node, anchor)
            }

        }

    },
}

function createItemNode(template, parentNode, insertAfterNode, localScope, parentScope) {

    const itemNode = template.cloneNode(true)
    const mergedScope = mergePreservingGetters({}, parentScope, localScope)

    /*
     * Annotate before parseDirectives so any nested <script arbor :prop> tags
     * can resolve their parent scope to this iteration's merged scope.
     */
    itemNode._qvScopeHolder = shallowRef(mergedScope)

    const cleanupCallbacks = parseDirectives(itemNode, mergedScope)

    if (insertAfterNode.nextSibling) {
        parentNode.insertBefore(itemNode, insertAfterNode.nextSibling)
    } else {
        parentNode.appendChild(itemNode)
    }

    reviveComponentsIn(itemNode)

    return {
        node: itemNode,
        cleanupCallbacks,
    }

}

function runCleanupCallbacks(callbacks) {

    for (const callback of callbacks) {
        callback()
    }

}

function removeRenderedItem(item) {

    const { node, cleanup } = item

    cleanupComponentsIn(node)
    runCleanupCallbacks(cleanup)

    if (node.parentNode) {
        node.parentNode.removeChild(node)
    }

}
